import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import ColumnHeader, { SortType } from '../../../ColumnHeader';
import Pagination from '../../../Pagination';
import useIsMobile from '../../../hooks/useIsMobile';
import HubError from '../../../Profile/SavedListsTabContent/HubError';
import RemoveFromListConfirmModal from '../../RemoveFromListConfirmModal';
import PlayerNoteModal from './PlayerNoteModal';
import SortPlayerListSheet from './SortPlayerListSheet';
import { removeSavedListPlayer, searchSavedListPlayers, updateSavedListPlayerNote } from '../../../../actions/saved-lists-actions';
import { showErrorToast } from '../../../../actions/notification-actions';
import { GENERIC_ERROR_TOAST_TITLE } from '../../listErrors';
import PlayerRow from './PlayerRow';
import PlayerCardMobile from './PlayerCardMobile';
import PlayersLoading from './PlayersLoading';
import { sortTypeToOrderByRule, normalizePlayer } from './helpers';
import { computeSkeletonRowCount } from '../../skeleton/computeSkeletonRowCount';
import { pluralize } from '../../../../utils/formatting';
import {
    PlayerSortColumn,
    SavedListOrderByRule,
    SAVED_LISTS_PAGE_SIZE,
    COLUMN_LABELS,
    UTR_COLUMN_LABEL,
    SINGLES_ICON,
    DOUBLES_ICON,
    SORT_ICON,
    EMPTY_MESSAGE,
    DEFAULT_SORT_LABEL,
    MOBILE_SORT_OPTIONS,
    LEGEND_LABEL,
    EXPAND_ALL_LABEL,
    COLLAPSE_ALL_LABEL,
} from './constants';
import styles from './playersList.scss';

const LOAD_ERROR_MESSAGE = 'We couldn\'t load these players. Please try again.';
const NOTE_ERROR_TOAST = {
    title: GENERIC_ERROR_TOAST_TITLE,
    message: 'We couldn\'t save this note. Please try again.',
};

const PlayersList = ({ listId, viewedAsOf, searchQuery, playerCount, onFirstLoadComplete, onPlayerRemoved, feedRefreshToken, 'data-testid': dataTestId }) => {
    const dispatch = useDispatch();
    const { isMobileWidth } = useIsMobile();

    const [rows, setRows] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [page, setPage] = useState(1);
    const [sortColumn, setSortColumn] = useState(null);
    const [sortType, setSortType] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const [playerPendingRemove, setPlayerPendingRemove] = useState(null);
    const [isRemoving, setIsRemoving] = useState(false);
    const [playerPendingNote, setPlayerPendingNote] = useState(null);
    const [isSavingNote, setIsSavingNote] = useState(false);
    const [isSortSheetOpen, setIsSortSheetOpen] = useState(false);
    // Which mobile cards are expanded (by row key). Owned here so the header's Expand All / Collapse All
    // can drive every card at once while each card's own See More / See Less still toggles just itself.
    const [expandedRows, setExpandedRows] = useState(() => new Set());

    // Monotonic token so only the newest feed response applies — a slower earlier request must not
    // paint over a faster later one (same guard the container and useSavedPlayerLists use).
    const requestIdRef = useRef(0);
    const isMountedRef = useRef(true);
    // Reported up to the container on the first successful feed page so it can enable Add Player +
    // Search and mark the list viewed. Owned there, not here: a list switch remounts this component,
    // so a guard living here would reset and mark-viewed would fire twice.
    const hasReportedFirstLoadRef = useRef(false);
    // Held in a ref so an unstable callback prop can't churn the fetch callback's identity.
    const onFirstLoadCompleteRef = useRef(onFirstLoadComplete);
    useEffect(() => {
        onFirstLoadCompleteRef.current = onFirstLoadComplete;
    }, [onFirstLoadComplete]);

    const base = dataTestId || 'list-detail.players';
    const rootTestId = base.replace(/\.players$/, '');

    // Pin the activity cursor to the value the visit started with. viewedAsOf is detail.lastViewedAt,
    // which the first feed load advances server-side (markSavedListViewed). Without this ref, a later
    // background header refresh (remove / Add Player) would pick up the advanced value, churn fetchFeed
    // into a redundant GET, and move the Recent Activity cursor mid-visit. A list switch remounts this
    // component, so the ref re-captures the new list's cursor.
    const viewedAsOfRef = useRef(viewedAsOf);

    const fetchFeed = useCallback(() => {
        requestIdRef.current += 1;
        const requestId = requestIdRef.current;
        setIsLoading(true);
        setIsError(false);
        return dispatch(searchSavedListPlayers({
            listId,
            search: searchQuery || undefined,
            orderBy: sortColumn || undefined,
            orderByRules: sortColumn ? sortTypeToOrderByRule(sortType) : undefined,
            page,
            size: SAVED_LISTS_PAGE_SIZE,
            viewedAsOf: viewedAsOfRef.current || undefined,
        }))
            .then((result) => {
                if (!isMountedRef.current || requestId !== requestIdRef.current) {
                    return;
                }
                const data = result?.data || {};
                setRows((Array.isArray(data.items) ? data.items : []).map(normalizePlayer));
                setExpandedRows(new Set());
                setTotalPages(data.totalPages ?? 0);
                setIsLoading(false);
                if (!hasReportedFirstLoadRef.current) {
                    hasReportedFirstLoadRef.current = true;
                    onFirstLoadCompleteRef.current();
                }
            })
            .catch(() => {
                if (!isMountedRef.current || requestId !== requestIdRef.current) {
                    return;
                }
                setIsError(true);
                setIsLoading(false);
            });
    }, [dispatch, listId, searchQuery, sortColumn, sortType, page]);

    useEffect(() => {
        fetchFeed();
    }, [fetchFeed]);

    // The container bumps feedRefreshToken when a mutation it owns (Add Player) needs the feed
    // reloaded in place — the header refreshes in the background (no remount), so nothing else would
    // pull the new row. Held via a ref + guarded past the initial mount so the token effect refetches
    // exactly once per bump without listing fetchFeed as a dep (which would double-fetch on sort/page).
    const fetchFeedRef = useRef(fetchFeed);
    useEffect(() => {
        fetchFeedRef.current = fetchFeed;
    }, [fetchFeed]);
    const hasMountedRef = useRef(false);
    useEffect(() => {
        if (!hasMountedRef.current) {
            hasMountedRef.current = true;
            return;
        }
        fetchFeedRef.current();
    }, [feedRefreshToken]);

    useEffect(() => () => {
        isMountedRef.current = false;
    }, []);

    // A new list resets the first-load guard and the sort/page cursor so the container re-gates its
    // controls (and re-marks viewed) until the new feed resolves.
    useEffect(() => {
        hasReportedFirstLoadRef.current = false;
        setPage(1);
        setSortColumn(null);
        setSortType(null);
    }, [listId]);

    // The container owns the search input; when its debounced query changes, restart at page 1.
    useEffect(() => {
        setPage(1);
    }, [searchQuery]);

    const handleSort = (column, type) => {
        setSortColumn(column);
        setSortType(type);
        setPage(1);
    };

    const handlePageChange = (nextPage) => {
        setPage(nextPage);
    };

    const handleMobileSort = (item) => {
        const type = item.orderByRules === SavedListOrderByRule.ASCENDING ? SortType.ASC : SortType.DESC;
        handleSort(item.orderBy, type);
        setIsSortSheetOpen(false);
    };

    const toggleRowExpanded = (rowKey) => {
        setExpandedRows((prev) => {
            const next = new Set(prev);
            if (next.has(rowKey)) {
                next.delete(rowKey);
            } else {
                next.add(rowKey);
            }
            return next;
        });
    };

    const rowKeys = rows.map((player, index) => player.id ?? index);
    const allExpanded = rowKeys.length > 0 && rowKeys.every(key => expandedRows.has(key));
    const handleToggleExpandAll = () => {
        setExpandedRows(allExpanded ? new Set() : new Set(rowKeys));
    };

    const closeRemoveModal = () => {
        setPlayerPendingRemove(null);
        setIsRemoving(false);
    };

    // The thunk owns the success/error toast (mirrors deleteSavedPlayerList); on success this refetches
    // the feed so the removed row drops, and notifies the container to refetch the "Showing N Players"
    // count. list-scoped — other lists keep the player. Shared by the desktop confirm modal and the
    // mobile row action (which removes directly, without a confirm step).
    const removePlayerFromList = (playerId) => {
        // getPlayerId returns playerId ?? id, so a feed item missing both would DELETE .../players/undefined
        // — guard the same way the row key does below rather than fire a malformed request.
        if (!playerId) {
            closeRemoveModal();
            return;
        }
        setIsRemoving(true);
        dispatch(removeSavedListPlayer(listId, playerId))
            .then(() => {
                closeRemoveModal();
                // Removing the last row on a later page would strand the user on an empty page (a false
                // "no players" state, and the pager vanishes once totalPages drops to 1). Step back a
                // page and let the fetchFeed effect (keyed on page) refetch; otherwise refetch in place.
                if (rows.length === 1 && page > 1) {
                    setPage(page - 1);
                } else {
                    fetchFeed();
                }
                onPlayerRemoved();
            })
            .catch(() => setIsRemoving(false));
    };

    const handleConfirmRemove = () => removePlayerFromList(playerPendingRemove?.id);

    const closeNoteModal = () => {
        setPlayerPendingNote(null);
        setIsSavingNote(false);
    };

    // Save and Clear share this path (Clear is a save of an empty note). On success it patches only the
    // edited row's note in place — which toggles the note indicator — with no fetchFeed/fetchDetail, per
    // the ticket's targeted-update requirement. On failure the modal stays open and re-enables so the
    // member can retry.
    const writeNote = (note) => {
        const playerId = playerPendingNote?.id;
        if (!playerId) {
            closeNoteModal();
            return;
        }
        setIsSavingNote(true);
        dispatch(updateSavedListPlayerNote(playerId, note))
            .then(() => {
                setRows(rs => rs.map(row => (row.id === playerId ? { ...row, note } : row)));
                closeNoteModal();
            })
            .catch(() => {
                setIsSavingNote(false);
                dispatch(showErrorToast(NOTE_ERROR_TOAST));
            });
    };

    const activeMobileOption = MOBILE_SORT_OPTIONS.find(option => (
        option.orderBy === sortColumn && option.orderByRules === sortTypeToOrderByRule(sortType)
    ));
    const currentMobileSortLabel = activeMobileOption ? activeMobileOption.label : DEFAULT_SORT_LABEL;
    const currentMobileSortValue = activeMobileOption ? activeMobileOption.value : '';

    const skeletonRowCount = computeSkeletonRowCount(playerCount, page, SAVED_LISTS_PAGE_SIZE);

    const renderColumnHeader = (label, column, initialSort, testIdSuffix, imgUrl = null) => (
        <ColumnHeader
            className={`${styles.colHeader} ${isLoading ? styles.colDisabled : ''}`}
            isSortable
            columnName={label}
            imgUrl={imgUrl}
            isSelected={sortColumn === column}
            sortType={sortType}
            initialSort={initialSort}
            onClick={type => !isLoading && handleSort(column, type)}
            data-testid={`${rootTestId}.${testIdSuffix}`}
        />
    );

    const renderDesktopHeader = () => (
        <div className={styles.tableHeader} data-testid={`${base}.table-header`}>
            <span className={styles.activityCol} />
            <div className={styles.nameCol}>
                {renderColumnHeader(COLUMN_LABELS.NAME, PlayerSortColumn.NAME, SortType.ASC, 'col-name')}
            </div>
            <div className={styles.singlesCol}>
                {renderColumnHeader(UTR_COLUMN_LABEL, PlayerSortColumn.SINGLES, SortType.DESC, 'col-singles', SINGLES_ICON)}
            </div>
            <div className={styles.doublesCol}>
                {renderColumnHeader(UTR_COLUMN_LABEL, PlayerSortColumn.DOUBLES, SortType.DESC, 'col-doubles', DOUBLES_ICON)}
            </div>
            <div className={styles.recentResultsCol}>
                {renderColumnHeader(COLUMN_LABELS.RECENT_RESULTS, PlayerSortColumn.ACTIVITY_RESULTS, SortType.DESC, 'col.recent-results')}
            </div>
            <div className={styles.eventActivityCol}>
                {renderColumnHeader(COLUMN_LABELS.EVENT_ACTIVITY, PlayerSortColumn.ACTIVITY_EVENTS, SortType.DESC, 'col.event-activity')}
            </div>
            <span className={styles.noteCol} />
            <div className={styles.menuCol} />
        </div>
    );

    const renderMobileControls = () => (
        <div className={styles.mobileControls}>
            <div className={styles.mobileSummaryRow}>
                <span className={styles.mobileCount} data-testid={`${rootTestId}.player-count`}>
                    {`Showing ${playerCount ?? 0} ${pluralize('Player', playerCount ?? 0)}`}
                </span>
                <button
                    type="button"
                    className={`${styles.sortAnchor} ${isLoading ? styles.sortAnchorDisabled : ''}`}
                    onClick={() => !isLoading && setIsSortSheetOpen(true)}
                    data-testid={`${rootTestId}.sort-trigger`}
                >
                    <img className={styles.sortIcon} src={SORT_ICON} alt="" data-testid={`${rootTestId}.sort-trigger.icon`} />
                    <span className={styles.sortAnchorLabel} data-testid={`${rootTestId}.sort-trigger.label`}>Sort by: </span>
                    {currentMobileSortLabel}
                </button>
            </div>
            <div className={styles.mobileDivider} />
            <div className={styles.mobileLegendRow}>
                <span className={styles.legend} data-testid={`${rootTestId}.activity-legend`}>
                    <span className={styles.legendDot} />
                    <span className={styles.legendLabel} data-testid={`${rootTestId}.activity-legend.label`}>
                        {LEGEND_LABEL}
                    </span>
                </span>
                <button
                    type="button"
                    className={`${styles.expandAll} ${isLoading ? styles.sortAnchorDisabled : ''}`}
                    onClick={() => !isLoading && handleToggleExpandAll()}
                    data-testid={`${rootTestId}.expand-all`}
                >
                    {allExpanded ? COLLAPSE_ALL_LABEL : EXPAND_ALL_LABEL}
                    <i className={`fas ${allExpanded ? 'fa-chevron-up' : 'fa-chevron-down'}`} />
                </button>
            </div>
        </div>
    );

    const renderRowsArea = () => {
        if (isLoading) {
            return (
                <PlayersLoading
                    rowCount={skeletonRowCount}
                    isMobile={isMobileWidth}
                    data-testid={`${base}-loading`}
                />
            );
        }
        if (rows.length === 0) {
            return (
                <div className={styles.empty} data-testid={`${base}-empty`}>
                    {EMPTY_MESSAGE}
                </div>
            );
        }
        return (
            <>
                {rows.map((player, index) => {
                    const rowKey = player.id ?? index;
                    const rowTestId = `${rootTestId}.player-row-${rowKey}`;
                    // Mobile removes straight away (no confirm modal, per Figma); desktop opens the
                    // confirmation modal before deleting.
                    const handleRemove = () => {
                        if (isMobileWidth) {
                            removePlayerFromList(player.id);
                        } else {
                            setPlayerPendingRemove(player);
                        }
                    };
                    const handleNote = () => setPlayerPendingNote(player);
                    return isMobileWidth
                        ? (
                            <PlayerCardMobile
                                key={rowKey}
                                player={player}
                                isExpanded={expandedRows.has(rowKey)}
                                onToggleExpand={() => toggleRowExpanded(rowKey)}
                                onNote={handleNote}
                                onRemove={handleRemove}
                                data-testid={rowTestId}
                            />
                        )
                        : <PlayerRow key={rowKey} player={player} onNote={handleNote} onRemove={handleRemove} data-testid={rowTestId} />;
                })}
            </>
        );
    };

    if (isError) {
        return (
            <div className={styles.playersList} data-testid={base}>
                <HubError
                    data-testid={`${base}.error`}
                    message={LOAD_ERROR_MESSAGE}
                    onRetry={fetchFeed}
                />
            </div>
        );
    }

    return (
        <div className={styles.playersList} data-testid={base}>
            {isMobileWidth ? renderMobileControls() : renderDesktopHeader()}
            <div className={styles.rows}>
                {renderRowsArea()}
            </div>
            {totalPages > 1 && (
                <div className={`${styles.paginationWrapper} ${isLoading ? styles.paginationDisabled : ''}`}>
                    <Pagination
                        currentPage={page}
                        lastPage={totalPages}
                        onClick={isLoading ? () => {} : handlePageChange}
                        data-testid={`${rootTestId}.pagination`}
                    />
                </div>
            )}
            {playerPendingRemove && (
                <RemoveFromListConfirmModal
                    isRemoving={isRemoving}
                    onConfirm={handleConfirmRemove}
                    onCancel={closeRemoveModal}
                />
            )}
            {playerPendingNote && (
                <PlayerNoteModal
                    initialNote={playerPendingNote.note || ''}
                    isSaving={isSavingNote}
                    onSave={writeNote}
                    onClear={() => writeNote('')}
                    onCancel={closeNoteModal}
                />
            )}
            {isSortSheetOpen && (
                <SortPlayerListSheet
                    options={MOBILE_SORT_OPTIONS}
                    selectedValue={currentMobileSortValue}
                    onApply={handleMobileSort}
                    onCancel={() => setIsSortSheetOpen(false)}
                    data-testid="sort-player-list"
                />
            )}
        </div>
    );
};

PlayersList.propTypes = {
    listId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    viewedAsOf: PropTypes.string,
    searchQuery: PropTypes.string,
    playerCount: PropTypes.number,
    onFirstLoadComplete: PropTypes.func,
    onPlayerRemoved: PropTypes.func,
    feedRefreshToken: PropTypes.number,
    'data-testid': PropTypes.string,
};

PlayersList.defaultProps = {
    viewedAsOf: null,
    searchQuery: '',
    playerCount: null,
    onFirstLoadComplete: () => {},
    onPlayerRemoved: () => {},
    feedRefreshToken: 0,
    'data-testid': null,
};

export default PlayersList;
